import React from 'react';
import ThyroidLoader from './ThyroidLoader';
import ThyroDoctorMascot, { BrandText } from './ThyroDoctorMascot';
import { patientAPI, authAPI } from '../../api';
// English / Hindi for patient pages (13 Oct 2026). Doctor and admin pages
// always get English from it.
import { useI18n, toEnglish, tNow } from '../../i18n';

// ─── Logo ──────────────────────────────────────────────────
// Icon = the "happy thyroid doctor" mascot (4 Oct 2026; replaced the old
// green star icon). On dark backgrounds (light=true) the mascot sits on a
// small white disc so its black outline stays visible.
const LOGO_MASCOT_HEIGHT = { sm: 32, md: 38, lg: 48 };
// Inside the left-hand panel of the patient, doctor and admin pages
// (.sidebar, 220px wide and fixed) the mascot sits ABOVE the name, so
// "INDIA THYROID CLINIC™" is no longer cut off at the panel's edge
// (14 Oct 2026). Everywhere else (mobile top bar, login, loading screen)
// the mascot stays beside the name as before.
export const Logo = ({ size = 'md', light = false }) => {
  const sizes = { sm: 13, md: 15, lg: 18 };
  const h = LOGO_MASCOT_HEIGHT[size] || LOGO_MASCOT_HEIGHT.md;
  const boxRef = React.useRef(null);
  const [inSidebar, setInSidebar] = React.useState(false);
  React.useLayoutEffect(() => {
    setInSidebar(!!(boxRef.current && boxRef.current.closest('.sidebar')));
  }, []);
  return (
    <div
      ref={boxRef}
      style={inSidebar
        ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0 }
        : { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
    >
      {light ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FFFFFF', borderRadius: 999, padding: '2px 4px', flexShrink: 0 }}>
          <ThyroDoctorMascot height={h - 4} title="" />
        </span>
      ) : (
        <ThyroDoctorMascot height={h} title="" />
      )}
      {/* ThyroConsult™ / by INDIA THYROID CLINIC™ / Online Thyroid Opinion Platform (6 Oct 2026) */}
      <BrandText
        fontSize={sizes[size] || sizes.md}
        fontFamily="var(--font-body)"
        color={light ? '#CECBF6' : 'var(--teal-600)'}
        subColor={light ? '#a9a5e0' : 'var(--text-secondary)'}
        taglineColor={light ? '#CECBF6' : '#111111'}
      />
    </div>
  );
};

// ─── Secure Badge ──────────────────────────────────────────
export const SecureBadge = () => {
  const { t } = useI18n();
  return (
  <span className="hipaa-badge">
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M6 1L1.5 3v3c0 2.76 1.98 5.34 4.5 6 2.52-.66 4.5-3.24 4.5-6V3L6 1z" fill="var(--teal-400)" opacity="0.2"/>
      <path d="M4 6l1.5 1.5L8 4" stroke="var(--teal-600)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    {t('Secure & Encrypted')}
  </span>
);
};

export const Spinner = ({ size = 20, color = 'var(--teal-400)', active = true, borderColor, stripColor }) => (
  <ThyroidLoader size={size} color={color} active={active} borderColor={borderColor} stripColor={stripColor} />
);

export const LoadingScreen = ({ message = 'Loading...' }) => (
  <div className="loading-screen">
    <Logo />
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <Spinner size={28} />
      <span style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>{tNow(message)}</span>
    </div>
  </div>
);

export const Alert = ({ type = 'info', children, onClose }) => (
  <div className={`alert alert-${type}`}>
    <span style={{ flex: 1 }}>{children}</span>
    {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6, fontSize: 16 }}>×</button>}
  </div>
);

// ─── Confirmation box (11 Oct 2026) ───────────────────────
// One look for every "are you sure?" box patients see (confirm doctor,
// confirm condition, final send to the doctor, reply to the doctor).
// Unlike window.confirm it can show a title and the agreed button words.
export const ConfirmDialog = ({ open, title, children, cancelLabel = 'Cancel', confirmLabel = 'Yes, continue', onCancel, onConfirm, busy = false }) => {
  const { t } = useI18n();
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? t(title) : undefined}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 14, width: '100%', maxWidth: 440, padding: '20px 22px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
        {title && <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>{typeof title === 'string' ? t(title) : title}</div>}
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>{children}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 18 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>{t(cancelLabel)}</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>
            {busy ? <Spinner size={16} color="#fff" /> : t(confirmLabel)}
          </button>
        </div>
      </div>
    </div>
  );
};

// Final warning before a questionnaire is sent (11 Oct 2026, owner-
// approved wording) — the same box at the end of Hypo, Hyper, Nodule,
// Thyroid Cancer and Other Problems.
export const FinalSendDialog = ({ open, onCancel, onConfirm, busy = false }) => {
  const { t } = useI18n();
  return (
    <ConfirmDialog open={open} title={t('Ready to send to your doctor?')} cancelLabel="Cancel" confirmLabel="Send to my doctor"
      onCancel={onCancel} onConfirm={onConfirm} busy={busy}>
      {t('Once sent, your answers cannot be changed and uploaded reports cannot be removed — unless your doctor asks you for more information.')}
    </ConfirmDialog>
  );
};

export const Badge = ({ variant = 'gray', children }) => (
  <span className={`badge badge-${variant}`}>{children}</span>
);

export const Avatar = ({ name = '?', size = 36, color = 'teal' }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const colors = {
    teal: { bg: 'var(--teal-50)', color: 'var(--teal-800)' },
    blue: { bg: 'var(--blue-50)', color: 'var(--blue-800)' },
    indigo: { bg: 'var(--indigo-50)', color: 'var(--indigo-800)' },
    amber: { bg: 'var(--amber-50)', color: 'var(--amber-800)' },
  };
  const c = colors[color] || colors.teal;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: c.bg, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 600, flexShrink: 0 }}>
      {initials}
    </div>
  );
};

export const IconButton = ({ onClick, title, children, variant = 'default' }) => (
  <button onClick={onClick} title={title} style={{ width: 30, height: 30, border: '1px solid var(--border-md)', borderRadius: 6, background: variant === 'danger' ? 'var(--red-50)' : 'var(--surface)', color: variant === 'danger' ? 'var(--red-600)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s' }}>
    {children}
  </button>
);

export const EmptyState = ({ icon, title, subtitle }) => (
  <div className="empty-state">
    <div className="empty-state-icon">{icon}</div>
    <div style={{ fontWeight: 500, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 13 }}>{subtitle}</div>
  </div>
);

export const AutoSave = ({ savedAt }) => {
  const { t } = useI18n();
  if (!savedAt) return null;
  return (
    <span className="autosave">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="var(--teal-600)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      {t('Saved')}{' '}{savedAt}
    </span>
  );
};

export const SectionHeader = ({ title, subtitle, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
    <div>
      <h2 style={{ fontSize: '1.4rem', marginBottom: 2 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 2 }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ─── Status Badge ──────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  const { t } = useI18n(); // English / Hindi (13 Oct 2026); English on doctor/admin pages
  const map = {
    completed: ['teal', 'Completed'],
    scheduled: ['blue', 'Scheduled'],
    in_progress: ['amber', 'In Progress'],
    cancelled: ['red', 'Cancelled'],
    pending: ['amber', 'Pending'],
    confirmed: ['teal', 'Confirmed'],
    failed: ['red', 'Failed'],
    refunded: ['gray', 'Refunded'],
    no_show: ['gray', 'No Show'],
    // Patient dashboard's per-episode workflow labels (conditionController.
    // getEpisodes' patientStatusLabel) — a separate axis from the above
    // appointment/invoice statuses and from patient_condition_episodes.status
    // (active/remission/monitoring/closed, the clinical state, never shown
    // via this badge).
    resume: ['amber', 'In progress'],
    submitted: ['blue', 'Submitted'],
    opinion_sought: ['teal', 'Opinion received'],
    // Episode exists but the initial doctor-selection/payment step was
    // never completed (Sept 2026) — see conditionController.getEpisodes'
    // patientStatusLabel and PatientPortal.js's completePaymentCondition.
    payment_pending: ['amber', 'Payment pending'],
  };
  const [variant, label] = map[status] || ['gray', status];
  return <Badge variant={variant}>{t(label)}</Badge>;
};

// ─── Date normalization for draft-loaded data ──────────────────────────────
// Postgres `date`/`timestamp` columns come back through node-postgres/
// Express as full ISO datetime strings ("2026-08-27T00:00:00.000Z"), not
// plain "yyyy-MM-dd" — but every <input type="date"> in every questionnaire
// requires exactly "yyyy-MM-dd" and silently rejects anything else (visible
// as a console warning, with the date field then showing blank). All four
// questionnaires merge the raw backend row straight into form state with no
// reformatting anywhere, so this walks the whole loaded object recursively
// (including nested objects/arrays — e.g. Hypo's {date, years, months}
// duration fields) and truncates any ISO-datetime-shaped string down to its
// date part. Non-date strings are left untouched.
function toDateInputValue(v) {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return v;
}
// ─── Local-calendar dates (26 Sep 2026) ───────────────────────────────────
// toISOString() converts to UTC first, so in India it returns YESTERDAY's
// date between midnight and 5:30 AM — "today" was rejected as a future
// date in that window. These use the browser's own calendar instead.
export function toLocalISODate(d) {
  if (!d || isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function todayLocalISO() {
  return toLocalISODate(new Date());
}
// EDD = LMP + 9 months + 7 days (fixed rule). The "YYYY-MM-DD" LMP is read
// as a LOCAL date (new Date("YYYY-MM-DD") reads it as UTC midnight, which
// is the previous evening for anyone west of UTC). Returns a local Date,
// or null. Same month-overflow behaviour as the previous setMonth/setDate
// code, so every EDD shown so far stays identical.
export function eddDateFromLmp(lmpIso) {
  const m = typeof lmpIso === 'string' && lmpIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1 + 9, parseInt(m[3], 10) + 7);
}

export function fixDatesDeep(obj) {
  if (Array.isArray(obj)) return obj.map(fixDatesDeep);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const key of Object.keys(obj)) out[key] = fixDatesDeep(obj[key]);
    return out;
  }
  return toDateInputValue(obj);
}

// ─── Shared single-box year/month/day grid date picker ─────────────────────
// Drop-in replacement for <input type="date">: same value contract
// ("yyyy-MM-dd" string in/out via onChange), so every existing call site
// that reads/writes plain date strings (fixDatesDeep above included) keeps
// working unmodified. One box, three swappable views (day is default;
// clicking the header zooms out to month, then year), plus direct
// DD/MM/YYYY typing that stays in sync with whichever view is open —
// confirmed against a mockup before being built for real.
const GDP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const GDP_MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const GDP_WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function gdpParseISO(v) {
  if (!v || typeof v !== 'string') return null;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: parseInt(m[1], 10), m: parseInt(m[2], 10), d: parseInt(m[3], 10) };
}
function gdpToISO(p) {
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}
function gdpFormatDMY(p) {
  if (!p) return '';
  return `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}/${p.y}`;
}
function gdpDaysInMonth(y, m) {
  return new Date(y, m, 0).getDate();
}
function gdpCompare(a, b) {
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
}

export const GridDatePicker = React.forwardRef(({ value, onChange, min, max, placeholder = 'DD/MM/YYYY', style, className, disabled }, forwardedRef) => {
  const { t } = useI18n();
  const wrapRef = React.useRef(null);
  const parsedValue = gdpParseISO(value);
  const today = new Date();
  const todayParts = { y: today.getFullYear(), m: today.getMonth() + 1, d: today.getDate() };
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState('day');
  const [cursor, setCursor] = React.useState(parsedValue || todayParts);
  const [yearPageStart, setYearPageStart] = React.useState((parsedValue || todayParts).y - 11);
  const [typedText, setTypedText] = React.useState(gdpFormatDMY(parsedValue));
  const [typedError, setTypedError] = React.useState('');

  // Draft-load / external value changes (e.g. Save-draft reload) must
  // re-sync the typed text and the cursor — value is the source of truth.
  React.useEffect(() => {
    const p = gdpParseISO(value);
    setTypedText(gdpFormatDMY(p));
    if (p) setCursor(p);
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setView('day');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const minP = gdpParseISO(min);
  const maxP = gdpParseISO(max);
  const inBounds = (p) => {
    if (minP && gdpCompare(p, minP) < 0) return false;
    if (maxP && gdpCompare(p, maxP) > 0) return false;
    return true;
  };

  const commit = (p) => {
    onChange(gdpToISO(p));
    setTypedText(gdpFormatDMY(p));
    setOpen(false);
    setView('day');
  };

  const handleTyped = (raw) => {
    setTypedText(raw);
    setTypedError('');
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return;
    const d = parseInt(m[1], 10), mo = parseInt(m[2], 10), y = parseInt(m[3], 10);
    if (mo < 1 || mo > 12) { setTypedError(t('Enter a valid month (01-12)')); return; }
    const maxDay = gdpDaysInMonth(y, mo);
    if (d < 1 || d > maxDay) { setTypedError(t('Enter a valid day for that month')); return; }
    const p = { y, m: mo, d };
    if (!inBounds(p)) { setTypedError(t('Date is outside the allowed range')); return; }
    setCursor(p);
    setYearPageStart(y - 6);
    onChange(gdpToISO(p));
  };

  const openPanel = () => {
    if (disabled) return;
    const p = parsedValue || todayParts;
    setCursor(p);
    setYearPageStart(p.y - 6);
    setView('day');
    setOpen(true);
  };

  const goPrev = () => {
    if (view === 'day') {
      let m = cursor.m - 1, y = cursor.y;
      if (m < 1) { m = 12; y -= 1; }
      setCursor({ y, m, d: cursor.d });
    } else if (view === 'month') {
      setCursor({ ...cursor, y: cursor.y - 1 });
    } else {
      setYearPageStart(s => s - 12);
    }
  };
  const goNext = () => {
    if (view === 'day') {
      let m = cursor.m + 1, y = cursor.y;
      if (m > 12) { m = 1; y += 1; }
      setCursor({ y, m, d: cursor.d });
    } else if (view === 'month') {
      setCursor({ ...cursor, y: cursor.y + 1 });
    } else {
      setYearPageStart(s => s + 12);
    }
  };
  const headerClick = () => {
    if (view === 'day') setView('month');
    else if (view === 'month') { setYearPageStart(cursor.y - 6); setView('year'); }
  };

  const headerLabel = view === 'day' ? `${t(GDP_MONTHS_FULL[cursor.m - 1])}, ${cursor.y}`
    : view === 'month' ? String(cursor.y)
    : `${yearPageStart}\u2013${yearPageStart + 11}`;

  const cellBase = { padding: '7px 0', fontSize: 12, borderRadius: 6, boxSizing: 'border-box' };

  const renderDayGrid = () => {
    const total = gdpDaysInMonth(cursor.y, cursor.m);
    const firstDow = new Date(cursor.y, cursor.m - 1, 1).getDay();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    const trailing = (7 - (cells.length % 7)) % 7;
    for (let i = 0; i < trailing; i++) cells.push(null);
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
          {GDP_WEEKDAYS.map(w => (
            <div key={w} style={{ fontSize: 10, textAlign: 'center', color: 'var(--teal-600)', fontWeight: 600 }}>{(() => { const k = `${w} (weekday)`; const r = t(k); return r === k ? w : r; })()}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((d, idx) => {
            if (d == null) return <div key={idx} />;
            const p = { y: cursor.y, m: cursor.m, d };
            const isSel = parsedValue && parsedValue.y === p.y && parsedValue.m === p.m && parsedValue.d === p.d;
            const isDisabled = !inBounds(p);
            return (
              <button key={idx} type="button" disabled={isDisabled} onClick={() => commit(p)} style={{
                ...cellBase,
                border: isSel ? '1.5px solid var(--teal-500)' : '1px solid transparent',
                background: isSel ? 'var(--teal-50)' : 'transparent',
                color: isDisabled ? '#ccc' : (isSel ? 'var(--teal-700)' : 'var(--text-primary)'),
                cursor: isDisabled ? 'default' : 'pointer',
              }}>{d}</button>
            );
          })}
        </div>
      </>
    );
  };

  const renderMonthGrid = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
      {GDP_MONTHS.map((label, idx) => {
        const m = idx + 1;
        const monthStart = { y: cursor.y, m, d: 1 };
        const monthEnd = { y: cursor.y, m, d: gdpDaysInMonth(cursor.y, m) };
        const isDisabled = (minP && gdpCompare(monthEnd, minP) < 0) || (maxP && gdpCompare(monthStart, maxP) > 0);
        const isSel = parsedValue && parsedValue.y === cursor.y && parsedValue.m === m;
        return (
          <button key={m} type="button" disabled={isDisabled}
            onClick={() => { setCursor({ ...cursor, m }); setView('day'); }}
            style={{
              ...cellBase,
              border: isSel ? '1.5px solid var(--teal-500)' : '1px solid var(--border)',
              background: isSel ? 'var(--teal-50)' : '#fff',
              color: isDisabled ? '#ccc' : (isSel ? 'var(--teal-700)' : 'var(--text-primary)'),
              cursor: isDisabled ? 'default' : 'pointer',
            }}>{t(label)}</button>
        );
      })}
    </div>
  );

  const renderYearGrid = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
      {Array.from({ length: 12 }, (_, i) => yearPageStart + i).map(y => {
        const yearStart = { y, m: 1, d: 1 };
        const yearEnd = { y, m: 12, d: 31 };
        const isDisabled = (minP && gdpCompare(yearEnd, minP) < 0) || (maxP && gdpCompare(yearStart, maxP) > 0);
        const isSel = parsedValue && parsedValue.y === y;
        return (
          <button key={y} type="button" disabled={isDisabled}
            onClick={() => { setCursor({ ...cursor, y }); setView('month'); }}
            style={{
              ...cellBase,
              border: isSel ? '1.5px solid var(--teal-500)' : '1px solid var(--border)',
              background: isSel ? 'var(--teal-50)' : '#fff',
              color: isDisabled ? '#ccc' : (isSel ? 'var(--teal-700)' : 'var(--text-primary)'),
              cursor: isDisabled ? 'default' : 'pointer',
            }}>{y}</button>
        );
      })}
    </div>
  );

  return (
    <div
      ref={el => { wrapRef.current = el; if (typeof forwardedRef === 'function') forwardedRef(el); else if (forwardedRef) forwardedRef.current = el; }}
      className={className}
      style={{ position: 'relative', display: 'inline-block', ...style }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', border: '1.5px solid var(--border)', borderRadius: 8,
        background: disabled ? 'var(--gray-100)' : '#fff', paddingRight: 8,
      }}>
        <input
          type="text"
          value={typedText}
          onChange={e => handleTyped(e.target.value)}
          onFocus={openPanel}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          data-hyporeq-type="date" data-hyporeq-filled={value ? 'true' : 'false'}
          style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 10px', fontSize: 13, background: 'transparent', minWidth: 0, width: '100%', boxSizing: 'border-box' }}
        />
        <span onClick={openPanel} aria-hidden="true" style={{ cursor: disabled ? 'default' : 'pointer', color: 'var(--text-tertiary)', fontSize: 11 }}>▾</span>
      </div>
      {typedError && <div style={{ fontSize: 11, color: 'var(--red-600, #c0392b)', marginTop: 3 }}>{typedError}</div>}
      {open && (
        <div style={{
          position: 'absolute', zIndex: 30, marginTop: 4, background: '#fff', border: '1px solid var(--border)',
          borderRadius: 8, padding: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', width: 232,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <button type="button" onClick={goPrev} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 8px' }}>‹</button>
            <button type="button" onClick={headerClick} disabled={view === 'year'} style={{
              border: 'none', background: 'none', cursor: view === 'year' ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 500, padding: '2px 8px', color: 'var(--text-primary)',
            }}>{headerLabel}</button>
            <button type="button" onClick={goNext} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 8px' }}>›</button>
          </div>
          {view === 'day' && renderDayGrid()}
          {view === 'month' && renderMonthGrid()}
          {view === 'year' && renderYearGrid()}
        </div>
      )}
    </div>
  );
});
GridDatePicker.displayName = 'GridDatePicker';

// ─── Before-you-start instructions (11 Oct 2026, owner-approved wording) ───
// Shared by RegisterPage.js and PatientPortal.js. Section 1 (patient)
// shows on registration's first screen, before the security check;
// section 2 (minor) once a minor is being registered; section 3 (adult
// relative) in "Opinion for a relative".
const keepReadyBoxStyle = { marginBottom: 18, padding: '14px 16px', border: '1px solid var(--teal-200, #a7d8cf)', borderRadius: 10, background: 'var(--teal-50, #eef8f6)', fontSize: 13, lineHeight: 1.55, color: 'var(--text-primary)' };
const keepReadyListStyle = { margin: '8px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 };

// Each item: [bold part, rest] — both translated (13 Oct 2026).
const KeepReadyItems = ({ items }) => {
  const { t } = useI18n();
  return (
    <ol style={keepReadyListStyle}>
      {items.map(([b, rest]) => <li key={b}><strong>{t(b)}</strong>{rest ? ` ${t(rest)}` : ''}</li>)}
    </ol>
  );
};

export function PatientKeepReadyBox() {
  const { t } = useI18n();
  return (
    <div style={keepReadyBoxStyle}>
      <strong>{t('Before you begin, please keep ready:')}</strong>
      <KeepReadyItems items={[
        ['Your own mobile phone and email.', 'A 7-digit code is sent to each; both must be verified on this first page.'],
        ['The patient in person.', 'The person registering must be the patient, or the parent/legal guardian of the minor patient. A live photo is taken with your camera — a photo of a photo, or of someone else, is not accepted.'],
        ['A device with a working camera,', 'in good light, with camera permission allowed in your browser.'],
        ['Your personal details:', 'full name, date of birth, gender and full address with PIN code.'],
        ['Your signature.', 'You will read the consents and sign on screen with your finger or mouse.'],
        ['A payment method:', "debit card, credit card, UPI or net banking. Payment is made on PayU's secure page; ThyroConsult does not see or store your card details. Keep any discount code ready."],
        ['Your reports and treatment details', '(listed before the questions start), for faster completion of the question session.'],
      ]} />
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        {t('If you stop midway, you can sign in and continue from the same step. An unfinished registration is deleted 7 days after your last sign-in.')}
      </div>
    </div>
  );
}

// fromDashboard: "Opinion for a relative" from the dashboard — the account
// holder is already verified and their own photo is already on file, so
// only the child's live photo is taken and no codes are sent (audit 12 Oct 2026).
export function MinorKeepReadyBox({ fromDashboard = false } = {}) {
  const { t } = useI18n();
  return (
    <div style={keepReadyBoxStyle}>
      <strong>{t('A parent/legal guardian must register a child. Please keep ready:')}</strong>
      <KeepReadyItems items={[
        ...(fromDashboard ? [
          ['The child in person, with you.', "A live photo of the child is taken; your own photo already on your profile is kept as the parent/legal guardian's photo. All messages about the child go to you."],
        ] : [
          ['The parent/legal guardian and the child together, at the same time.', 'Two live photos are taken: one of the child and one of the parent/legal guardian.'],
          ["The parent/legal guardian's own mobile phone and email", 'for the verification codes. All messages about the child go to the parent/legal guardian.'],
        ]),
        ["The child's details:", 'full name, date of birth and gender.'],
        ["The parent/legal guardian's details", 'and relationship to the child.'],
        ["The parent/legal guardian's signature.", 'The parent/legal guardian signs every consent, including the Parent/legal guardian declaration.'],
        ['A payment method', '(debit card, credit card, UPI or net banking). The receipt reads "Received from [parent/legal guardian] on behalf of [child] for online opinion purpose".'],
      ]} />
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        {t("Before uploading any of the child's reports, the parent/legal guardian gives a separate consent for the upload.")}
      </div>
    </div>
  );
}

export function RelativeKeepReadyBox() {
  const { t } = useI18n();
  return (
    <div style={keepReadyBoxStyle}>
      <strong>{t('To add a relative, please keep ready:')}</strong>
      <KeepReadyItems items={[
        ['The relative in person, with you.', 'A live photo of the relative is taken — not a photo of a photo.'],
        ["The relative's details:", 'full name, date of birth, gender and relationship to you.'],
        ["The relative's own consent.", 'The relative reads and signs the consents themselves.'],
        ['A payment method', "for the relative's online opinion (debit card, credit card, UPI or net banking)."],
      ]} />
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        {t("Messages about the relative's online opinion are sent to your email and mobile.")}
      </div>
    </div>
  );
}

// Section 6 — the screen before the first question (11 Oct 2026). Shown
// once, while the request's questionnaire is still 'not_started'. The
// thyroid reports table is left out for "Other Problems".
const BEFORE_Q_REPORTS = [
  ['Blood reports', 'TSH, T3, FT3, T4, FT4, Anti-TPO, Anti-Tg, TRAb / TSI, Tg / TgAb, CBC, Vitamin B12, Vitamin D3, calcium, blood sugar, lipid profile, iron studies'],
  ['Imaging', 'Thyroid / Neck ultrasound, Tc99m-Thyroid scan, I-131 Wholebody Radioiodine scan, CT-neck, MRI-neck'],
  ['Biopsy & IHC reports', 'FNAC / Biopsy report, Histopathology Report (HPR)'],
  ['Heart and bone tests', 'ECG, 2D-Echo, Holter, DEXA scan (if done)'],
  ['Treatment details', 'Thyroid surgery date and type; radioiodine (RAI) doses and dates; current medicines with brand, dose and timing'],
  ['Notes from other doctors', "Prescriptions, earlier doctors' notes and advice, discharge summaries, referral letters"],
  ['For women', 'Date of your last menstrual period (LMP)'],
];
export function BeforeQuestionnaireScreen({ showThyroidReports = true, onStart }) {
  const { t } = useI18n();
  const cell = { padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', textAlign: 'left' };
  return (
    <div style={{ padding: '8px 0 4px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-primary)' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 14 }}>{t('Before you start')}</div>
      <div style={{ padding: '12px 14px', borderLeft: '4px solid #d97706', background: '#fffbeb', borderRadius: 8, marginBottom: 18 }}>
        <strong>{t('Please answer each question carefully and in order.')}</strong>{' '}{t('Once you click')}{' '}<strong>{t('Next')}</strong>{t(', you will not be able to go back to the earlier questions, so take extra care before clicking Next.')}
        {/* moved here from the first question (owner, 15 Oct 2026) */}
        <div style={{ marginTop: 6 }}>{t('All questions are to be answered before clicking on the Next button.')}</div>
      </div>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('Please keep all available reports ready to upload as you go:')}</div>
      {showThyroidReports ? (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 8 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                <th style={{ ...cell, fontWeight: 600, width: '32%' }}>{t('What')}</th>
                <th style={{ ...cell, fontWeight: 600 }}>{t('Examples')}</th>
              </tr>
            </thead>
            <tbody>
              {BEFORE_Q_REPORTS.map(([what, ex]) => (
                <tr key={what}><td style={{ ...cell, fontWeight: 500 }}>{t(what)}</td><td style={cell}>{t(ex)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>{t('Your reports, prescriptions and notes from other doctors about this problem.')}</div>
      )}
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('Upload rules:')}</div>
      <ul style={{ margin: '0 0 20px', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <li>{t('Accepted files: PDF, images (any format), Word and Excel files.')}</li>
        <li>{t('Maximum size:')}{' '}{UPLOAD_MAX_MB}{' '}{t('MB per file.')}</li>
        <li>{t('Photograph every page of a report, flat, in good light, with all values readable.')}</li>
        <li>{t(UPLOAD_SIZE_TIP)}</li>
        <li>{t('Your answers save automatically. You can stop and continue later from where you left.')}</li>
      </ul>
      <div style={{ textAlign: 'center' }}>
        <button type="button" className="btn btn-primary btn-lg" onClick={onStart}>{t('Start the questions →')}</button>
      </div>
    </div>
  );
}

// ─── Decimal clamp for numeric lab-value inputs ────────────────────────────
// Caps any numeric field (test values, reference ranges, etc.) at a maximum
// of 2 decimal places — a 3rd decimal digit is stripped the moment it would
// be typed, not just rounded afterward. Intermediate typing states ("15.",
// "15.1", "", "-") pass through untouched so it never fights the user mid-entry.
export function clampDecimals(value, maxDecimals = 2) {
  if (value == null) return value;
  const str = String(value);
  const match = str.match(/^(-?\d*)\.(\d*)$/);
  if (!match) return str; // no decimal point yet, or not a plain number shape — leave as-is
  const [, intPart, decPart] = match;
  if (decPart.length <= maxDecimals) return str;
  return `${intPart}.${decPart.slice(0, maxDecimals)}`;
}

// ─── Accepted file types for uploads ────────────────────────────────────────
// Shared by LabReportUpload and AdditionalDocumentsUploader so the accepted
// set can't drift between the two. HEIC/HEIF gets the same extension-based
// fallback as the backend's documentFilter (security.js) — browsers report
// inconsistent/wrong MIME types for it, so file.type alone isn't reliable;
// checking the filename extension too catches those cases the same way.
// 11 Oct 2026 (owner's rule): PDF, images (all common formats), Word and
// Excel — max 1 MB each. Same list as the backend's security.js. SVG is
// left out on purpose (it can carry script).
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/x-ms-bmp',
  'image/tiff', 'image/avif', 'image/heic', 'image/heif',
];
const ACCEPTED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx',
  '.jpg', '.jpeg', '.jpe', '.jfif', '.png', '.webp', '.gif', '.bmp', '.tif', '.tiff', '.avif', '.heic', '.heif',
];
// For the file pickers' accept="" attribute.
export const UPLOAD_ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');
// One wording for every upload message.
export const UPLOAD_TYPES_TEXT = 'PDF, images, Word or Excel files';
export const UPLOAD_SIZE_TIP = 'File too big? Take the photo at a lower camera resolution, or use your phone\'s "scan to PDF" or a free online compressor, and upload each page as a separate file.';
export const UPLOAD_MAX_MB = 1;
export function isAcceptedFile(file) {
  if (ACCEPTED_MIME_TYPES.includes(file.type)) return true;
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  return ACCEPTED_EXTENSIONS.includes(ext);
}

// ─── Document capture helpers ──────────────────────────────
// Shared by LabReportUpload and AdditionalDocumentsUploader so every
// upload point on every questionnaire (Hypo/Hyper/TC/Nodule) behaves
// identically: drag & drop, or three explicit picker buttons (Camera /
// Gallery / Local device). Hard limit 1 MB per file (11 Oct 2026, was
// 5 MB) — same as the backend's security.js. Photos are compressed here
// first: JPEG quality is lowered (down to 0.5); only if that is still over
// 1 MB is the picture made smaller, never below 1600 px on its long side,
// so lab values and small print stay readable. PDFs, Word and Excel files
// pass through untouched.
const MAX_UPLOAD_BYTES = UPLOAD_MAX_MB * 1024 * 1024; // hard cap — matches backend
const IMAGE_COMPRESS_TARGET_BYTES = MAX_UPLOAD_BYTES;   // photos are squeezed to fit it

function canvasToBlob(canvas, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
}

function loadImageForCanvas(file) {
  // Prefer createImageBitmap (fast, works off the main thread's decode),
  // fall back to a plain <img> for older/limited WebViews (relevant since
  // patients are on a wide range of Android devices/browsers).
  if (window.createImageBitmap) {
    return window.createImageBitmap(file).catch(() => null);
  }
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

// Leaves 2% headroom under the 1 MB cap for the upload's own overhead.
export async function compressImageIfNeeded(file, targetBytes = Math.floor(IMAGE_COMPRESS_TARGET_BYTES * 0.98)) {
  if (!file.type.startsWith('image/') || file.size <= targetBytes) return file;

  const bitmap = await loadImageForCanvas(file);
  if (!bitmap) return file; // couldn't decode here — let the backend/original file through as-is
  // The decoded picture is always released at the end, even on an error
  // (audit 12 Oct 2026 — large photos use a lot of phone memory).
  let blob = null;
  try {
    let width = bitmap.width || bitmap.naturalWidth;
    let height = bitmap.height || bitmap.naturalHeight;
    if (!width || !height) return file;
    // Phones with 48–108 MP cameras: iPhone Safari cannot draw a picture
    // larger than about 16 million pixels, so very large photos are first
    // made smaller to that size (audit 12 Oct 2026).
    const MAX_PIXELS = 16000000;
    if (width * height > MAX_PIXELS) {
      const k = Math.sqrt(MAX_PIXELS / (width * height));
      width = Math.floor(width * k); height = Math.floor(height * k);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // White background first: a transparent PNG would otherwise turn black
    // as a JPEG and dark text on it could not be read (audit 12 Oct 2026).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    // Reduce JPEG quality only (never resolution) until under the target,
    // or until quality would drop low enough to risk illegible reports.
    let quality = 0.9;
    blob = await canvasToBlob(canvas, quality);
    while (blob && blob.size > targetBytes && quality > 0.55) { // stops at 0.5 (float-safe)
      quality -= 0.1;
      blob = await canvasToBlob(canvas, quality);
    }
    // Still over 1 MB at quality 0.5 (large phone photos): make the picture
    // smaller step by step, but never below 1600 px on its long side.
    const MIN_LONG_SIDE = 1600;
    let w = width, h = height;
    while (blob && blob.size > targetBytes && Math.max(w, h) > MIN_LONG_SIDE) {
      const scale = Math.max(0.8, MIN_LONG_SIDE / Math.max(w, h));
      w = Math.round(w * scale); h = Math.round(h * scale);
      canvas.width = w; canvas.height = h;
      const c2 = canvas.getContext('2d');
      c2.fillStyle = '#ffffff';
      c2.fillRect(0, 0, w, h);
      c2.drawImage(bitmap, 0, 0, w, h);
      blob = await canvasToBlob(canvas, 0.7);
      if (blob && blob.size > targetBytes) blob = await canvasToBlob(canvas, 0.5);
    }
  } catch (e) {
    blob = null;
  } finally {
    if (bitmap.close) { try { bitmap.close(); } catch (e) { /* ignore */ } }
  }
  // never send a "compressed" file that came out bigger than the original
  if (!blob || blob.size >= file.size) return file;

  const compressedName = file.name.replace(/\.\w+$/, '') + '.jpg';
  return new File([blob], compressedName, { type: 'image/jpeg', lastModified: Date.now() });
}

// Drag & drop zone + three picker buttons (Camera / Gallery / Local device).
// `onFiles` receives a FileList/array exactly like a native <input> change.
// Real camera access via getUserMedia — works on desktop (webcam) and
// mobile alike, unlike a plain <input type="file" capture="..."> which
// only mobile browsers act on at all. facingMode is an { ideal: ... }
// preference, not a hard requirement, so it gracefully falls back to
// whatever single camera a laptop has instead of erroring out — on
// phones/tablets, which have both, it correctly prefers the rear camera
// since this captures documents/reports, not a selfie (the opposite of
// PatientPortal.js's RelativeOnboardingFlow, which deliberately uses the
// front camera for a live ID photo). Mirrors that same proven
// stream-lifecycle pattern: video ref, canvas capture, explicit track
// cleanup on close/unmount.
const CameraCaptureModal = ({ onCapture, onClose }) => {
  const { t } = useI18n();
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [captured, setCaptured] = React.useState(null); // data URL preview
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // no camera support in this browser (audit 12 Oct 2026)
      setError('Could not access the camera. Check browser permissions and try again.');
      return undefined;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError('Could not access the camera. Check browser permissions and try again.'));
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL('image/jpeg', 0.9));
  };

  const confirm = async () => {
    const blob = await (await fetch(captured)).blob();
    const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
    streamRef.current?.getTracks().forEach(t => t.stop());
    onCapture(file);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg-primary, #fff)', borderRadius: 16, width: '100%', maxWidth: 420, padding: 20, textAlign: 'center' }}>
        {error ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--red-600, #dc2626)', marginBottom: 14 }}>⚠️ {t(error)}</div>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '0.5px solid var(--border, #d0d7e8)', borderRadius: 8, background: 'none', cursor: 'pointer' }}>{t('Close')}</button>
          </>
        ) : !captured ? (
          <>
            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 8, marginBottom: 12, background: '#000' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px', border: '0.5px solid var(--border, #d0d7e8)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13 }}>{t('Cancel')}</button>
              <button type="button" onClick={capturePhoto} style={{ flex: 1, padding: '9px', border: 'none', borderRadius: 8, background: 'var(--teal-600, #0f766e)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>{t('Capture')}</button>
            </div>
          </>
        ) : (
          <>
            <img src={captured} alt={t('Captured')} style={{ width: '100%', borderRadius: 8, marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setCaptured(null)} style={{ flex: 1, padding: '9px', border: '0.5px solid var(--border, #d0d7e8)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13 }}>{t('Retake')}</button>
              <button type="button" onClick={confirm} style={{ flex: 1, padding: '9px', border: 'none', borderRadius: 8, background: 'var(--teal-600, #0f766e)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>{t('Use this photo')}</button>
            </div>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

const UploadControls = ({ onFiles, disabled, hint }) => {
  const { t } = useI18n();
  const [dragOver, setDragOver] = React.useState(false);
  const [showCamera, setShowCamera] = React.useState(false);
  const galleryRef = React.useRef(null);
  const localRef = React.useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files);
  };

  const handleCameraCapture = (file) => {
    setShowCamera(false);
    onFiles([file]);
  };

  const pickerBtnStyle = {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    padding: '8px 4px', border: '0.5px solid var(--border, #d0d7e8)', borderRadius: 8,
    background: 'var(--bg-primary, #fff)', fontSize: 11, color: 'var(--text-secondary, #666)',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 14,
          border: `1.5px dashed ${dragOver ? 'var(--teal-400, #3a7bd5)' : '#ccc'}`,
          borderRadius: 8, background: dragOver ? 'var(--teal-50, #f0f6ff)' : 'var(--gray-50, #fafafa)',
          fontSize: 12, color: 'var(--text-tertiary, #888)', textAlign: 'center', marginBottom: 8,
        }}
      >
        <span>{t('Drag & drop a file here')}</span>
        {hint && <span style={{ fontSize: 11, marginTop: 2 }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" style={pickerBtnStyle} disabled={disabled} onClick={() => setShowCamera(true)}>
          <span style={{ fontSize: 15 }} aria-hidden="true">📷</span>{' '}{t('Camera')}
        </button>
        <button type="button" style={pickerBtnStyle} disabled={disabled} onClick={() => galleryRef.current?.click()}>
          <span style={{ fontSize: 15 }} aria-hidden="true">🖼️</span>{' '}{t('Gallery')}
        </button>
        <button type="button" style={pickerBtnStyle} disabled={disabled} onClick={() => localRef.current?.click()}>
          <span style={{ fontSize: 15 }} aria-hidden="true">📁</span>{' '}{t('Local device')}
        </button>
      </div>
      {showCamera && <CameraCaptureModal onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />}
      {/* Gallery: image picker, no capture attribute so it opens the photo library, not the camera */}
      <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={disabled}
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }} />
      {/* Local device: full file browser, includes PDFs */}
      <input ref={localRef} type="file" accept={UPLOAD_ACCEPT_ATTR} multiple style={{ display: 'none' }} disabled={disabled}
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }} />
    </div>
  );
};

// ─── "<" / ">" in front of a lab value (13 Oct 2026) ───────────────────
// Reports print some results as "<0.005" or ">40". The number itself is
// still typed in the normal number box; this small selector sits in front
// of it. Every questionnaire keeps the signs in ONE object, keyed by the
// database column of the value — e.g. { tsh_value: '<', tgab_value: '>' } —
// saved as value_qualifiers. No entry means "=" (the exact value).
// ≤ and ≥ too (13 Oct 2026); "<=", "< or =", "<&=" (and the same with >)
// are read as ≤ / ≥ by normaliseLabQualifier.
export const LAB_QUALIFIER_OPTIONS = [['', '='], ['<', '<'], ['≤', '≤'], ['>', '>'], ['≥', '≥']];
export const normaliseLabQualifier = (q) => {
  const t = String(q == null ? '' : q).trim().toLowerCase().replace(/\s+/g, '');
  if (t === '<' || t === '>' || t === '≤' || t === '≥') return t;
  if (/^(<(=|&=|or=)|=<)$/.test(t)) return '≤';
  if (/^(>(=|&=|or=)|=>)$/.test(t)) return '≥';
  return '';
};
export const cleanLabQualifier = normaliseLabQualifier;
export const LabValueQualifier = ({ value, onChange, disabled }) => {
  const { t } = useI18n();
  return (
  <select className="form-input" aria-label={t('Exact value, less than, less than or equal to, more than, or more than or equal to')}
    title={t('= exact value · < less than · ≤ less than or equal to · > more than · ≥ more than or equal to (as printed on the report)')}
    value={cleanLabQualifier(value)} disabled={disabled}
    onChange={e => onChange(cleanLabQualifier(e.target.value))}
    style={{ width: 56, minWidth: 56, flex: '0 0 56px', fontSize: 14, padding: '6px 4px', textAlign: 'center' }}>
    {LAB_QUALIFIER_OPTIONS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
  </select>
);
};
// returns a NEW qualifiers object with column `col` set to q (or removed for "=")
export const setLabQualifier = (map, col, q) => {
  const next = { ...((map && typeof map === 'object') ? map : {}) };
  const c = cleanLabQualifier(q); // stored as < ≤ > ≥ only, never "<=" (audit 13 Oct 2026)
  if (c) next[col] = c; else delete next[col];
  return next;
};
// "<0.005" for output sentences; the value unchanged when there is no sign
export const withLabQualifier = (q, value) => (
  value === null || value === undefined || value === '' ? value : `${cleanLabQualifier(q)}${value}`
);

// ─── Parent/legal guardian consent before a minor's reports are uploaded ──
// (A8, 13 Oct 2026; standing rule). For a child's profile the upload
// buttons stay disabled until the parent/legal guardian ticks this box;
// the server refuses the upload without it as well
// (backend services/guardianUploadConsent.js — same wording there).
// The answer is fetched once per profile and shared by every upload box
// on the page.
export const GUARDIAN_UPLOAD_CONSENT_TEXT = "I am the parent/legal guardian of this child. I consent to uploading the child's medical reports and documents to ThyroConsult, to be used only for the child's online opinion.";
const guardianConsentCache = {}; // profile id → { status, promise, listeners }
function activeProfileKey() {
  try {
    const token = authAPI.getToken();
    if (!token) return '';
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part)).sub || token;
  } catch (e) {
    return '';
  }
}
function guardianConsentEntry(key) {
  if (!guardianConsentCache[key]) guardianConsentCache[key] = { status: null, promise: null, listeners: new Set() };
  return guardianConsentCache[key];
}
function publishGuardianConsent(key, status) {
  const entry = guardianConsentEntry(key);
  entry.status = status;
  entry.listeners.forEach(fn => fn(status));
}
export function useGuardianUploadConsent() {
  const key = activeProfileKey();
  const [status, setStatus] = React.useState(() => (guardianConsentCache[key] && guardianConsentCache[key].status) || null);
  React.useEffect(() => {
    if (!key) { setStatus({ required: false, given: false }); return undefined; }
    const entry = guardianConsentEntry(key);
    entry.listeners.add(setStatus);
    if (entry.status) setStatus(entry.status);
    else if (!entry.promise) {
      entry.promise = patientAPI.getGuardianUploadConsent()
        .then(s => publishGuardianConsent(key, { required: !!(s && s.required), given: !!(s && s.given) }))
        // not known: let the upload try — the server still checks it
        .catch(() => { entry.promise = null; setStatus({ required: false, given: false }); });
    }
    return () => { entry.listeners.delete(setStatus); };
  }, [key]);
  const give = React.useCallback(async () => {
    const s = await patientAPI.giveGuardianUploadConsent();
    const next = { required: !!(s && s.required), given: !!(s && s.given) };
    if (key) publishGuardianConsent(key, next);
    return next;
  }, [key]);
  // the server said the consent is missing (e.g. the first check failed)
  const markNeeded = React.useCallback(() => { if (key) publishGuardianConsent(key, { required: true, given: false }); }, [key]);
  const blocked = !!(status && status.required && !status.given);
  return { status, blocked, give, markNeeded };
}
export const GuardianUploadConsentBox = ({ consent }) => {
  const { t } = useI18n();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  if (!consent || !consent.blocked) return null;
  const tick = async () => {
    setBusy(true); setErr('');
    try { await consent.give(); }
    catch (e) { setErr(e.response?.data?.error || t('Could not save the consent. Please try again.')); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, fontSize: 12.5, lineHeight: 1.5 }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: busy ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={false} disabled={busy} onChange={tick} style={{ marginTop: 3 }} />
        <span>{t(GUARDIAN_UPLOAD_CONSENT_TEXT)}</span>
      </label>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('Uploading is enabled once this is ticked. The date, time and IP address are recorded.')}</div>
      {busy && <div style={{ marginTop: 4 }}><Spinner size={12} /></div>}
      {err && <div style={{ color: 'var(--red-600)', marginTop: 4 }}>{t(err)}</div>}
    </div>
  );
};

export const LabReportUpload = ({ patientId, episodeId, fieldLabel, category = 'blood_report', reports = [], onReportsChange, onExtract, enableExtract = true }) => {
  const { t } = useI18n();
  const [uploading, setUploading] = React.useState(false);
  const [extractingId, setExtractingId] = React.useState(null);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState(''); // what auto-fill changed or couldn't read (P4, 13 Oct 2026)
  const guardianConsent = useGuardianUploadConsent(); // child's profile: consent first (A8)

  const handleFiles = async (fileList) => {
    if (guardianConsent.blocked) return;
    const files = Array.from(fileList || []);
    // the list is built up here, so every file of a multi-file pick is kept
    // (audit 12 Oct 2026 — only the last one was before)
    let acc = [...(reports || [])];
    for (let file of files) {
      if (!isAcceptedFile(file)) { setError(t('Only {types} can be uploaded.', { types: t(UPLOAD_TYPES_TEXT) })); continue; }
      setUploading(true); setError('');
      try {
        file = await compressImageIfNeeded(file);
        if (file.size > MAX_UPLOAD_BYTES) { setError(`${t('"{name}" is larger than {mb} MB.', { name: file.name, mb: UPLOAD_MAX_MB })} ${t(UPLOAD_SIZE_TIP)}`); setUploading(false); continue; }
        const formData = new FormData();
        formData.append('document', file);
        formData.append('patientId', patientId);
        formData.append('category', category);
        if (episodeId) formData.append('episodeId', episodeId);
        formData.append('fieldLabel', toEnglish(fieldLabel)); // always English for the server (13 Oct 2026)
        const res = await patientAPI.uploadDocument(formData);
        acc = [...acc, { documentId: res.documentId, fileName: file.name }];
        onReportsChange(acc);
      } catch (err) {
        if (err.response?.data?.code === 'GUARDIAN_UPLOAD_CONSENT_REQUIRED') guardianConsent.markNeeded();
        setError(err.response?.data?.error || 'Upload failed');
      } finally {
        setUploading(false);
      }
    }
  };

  const runExtract = async (documentId) => {
    setExtractingId(documentId); setError(''); setNote('');
    try {
      const res = await patientAPI.extractDocumentFields(documentId, toEnglish(fieldLabel));
      if (!res.found) {
        setError(t('Couldn\'t find "{field}" on that report — please enter values manually', { field: t(fieldLabel) }));
      } else if (onExtract) {
        onExtract(res);
        // A printed "<0.005" comes back as the number 0.005 with its "<"
        // (filled into the small selector in front of the value); a value
        // the reader couldn't read at all is left blank.
        const unreadable = res.unreadable || [];
        setNote(unreadable.length ? t(unreadable.length > 1 ? 'Couldn\'t read {fields} from the report — please type these in yourself.' : 'Couldn\'t read {fields} from the report — please type it in yourself.', { fields: unreadable.join(', ') }) : '');
      }
    } catch (err) {
      setError('Auto-fill failed — please enter values manually');
    } finally {
      setExtractingId(null);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {uploading ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12,
          border: '1px dashed var(--border-md)', borderRadius: 'var(--radius-md)',
          background: 'var(--gray-50)', fontSize: 12, color: 'var(--text-tertiary)',
        }}>
          <Spinner size={14} />{' '}{t('Uploading…')}
        </div>
      ) : (
        <>
          <GuardianUploadConsentBox consent={guardianConsent} />
          <UploadControls
            disabled={uploading || guardianConsent.blocked}
            onFiles={handleFiles}
            hint={`${t('{field} report ({types} · max {mb} MB each)', { field: t(fieldLabel), types: t(UPLOAD_TYPES_TEXT), mb: UPLOAD_MAX_MB })}${enableExtract ? t(' — AI can auto-extract values') : ''}`}
          />
        </>
      )}

      {reports?.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {reports.map(r => (
            <div key={r.documentId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--teal-200)', borderRadius: 'var(--radius-md)', background: 'var(--teal-50)', fontSize: 12 }}>
              <span>📄</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.fileName}</span>
              <span style={{ color: 'var(--teal-600)' }}>✓</span>
              {enableExtract && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={extractingId === r.documentId}
                  onClick={() => runExtract(r.documentId)}>
                  {extractingId === r.documentId ? <Spinner size={12} /> : t('🤖 Auto-fill from this')}
                </button>
              )}
              {/* Removes this report from the field's list only — does not
                  delete the underlying uploaded document from storage,
                  same as removing a surgery/therapy entry elsewhere just
                  drops it from that array. */}
              <button type="button" title={t('Remove this report')}
                onClick={() => onReportsChange((reports || []).filter(x => x.documentId !== r.documentId))}
                style={{ background: 'none', border: 'none', color: 'var(--red-500, #ef4444)', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ fontSize: 11, color: 'var(--red-600)', marginTop: 4 }}>{t(error)}</div>}
      {note && <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 8px', marginTop: 4 }}>{note}</div>}
    </div>
  );
};

export const AdditionalDocumentsUploader = ({ patientId, episodeId, category = 'other' }) => {
  const { t } = useI18n();
  const [items, setItems] = React.useState([]);
  const guardianConsent = useGuardianUploadConsent(); // child's profile: consent first (A8)

  const uploadOne = async (file) => {
    const key = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    if (!isAcceptedFile(file)) {
      setItems(p => [...p, { key, fileName: file.name, status: 'error', error: t('Only {types} can be uploaded.', { types: t(UPLOAD_TYPES_TEXT) }) }]);
      return;
    }

    setItems(p => [...p, { key, fileName: file.name, status: 'uploading' }]);
    try {
      const compressed = await compressImageIfNeeded(file);
      if (compressed.size > MAX_UPLOAD_BYTES) {
        setItems(p => p.map(i => i.key === key ? { ...i, status: 'error', error: `${t('Larger than {mb} MB.', { mb: UPLOAD_MAX_MB })} ${t(UPLOAD_SIZE_TIP)}` } : i));
        return;
      }
      const formData = new FormData();
      formData.append('document', compressed);
      formData.append('patientId', patientId);
      formData.append('category', category);
      if (episodeId) formData.append('episodeId', episodeId);
      formData.append('fieldLabel', 'Additional document');

      const res = await patientAPI.uploadDocument(formData);
      setItems(p => p.map(i => i.key === key ? { ...i, status: 'done', documentId: res.documentId } : i));
    } catch (err) {
      if (err.response?.data?.code === 'GUARDIAN_UPLOAD_CONSENT_REQUIRED') guardianConsent.markNeeded();
      setItems(p => p.map(i => i.key === key
        ? { ...i, status: 'error', error: err.response?.data?.error || 'Upload failed' }
        : i));
    }
  };

  const handleFiles = (fileList) => {
    if (guardianConsent.blocked) return;
    Array.from(fileList || []).forEach(uploadOne);
  };

  return (
    <div style={{ marginTop: 12 }}>
      <GuardianUploadConsentBox consent={guardianConsent} />
      <UploadControls
        disabled={guardianConsent.blocked}
        onFiles={handleFiles}
        hint={t('Doctor\'s notes, prescriptions, or anything else — {types} · max {mb} MB each', { types: t(UPLOAD_TYPES_TEXT), mb: UPLOAD_MAX_MB })}
      />

      {items.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}>
              <span>📄</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fileName}</span>
              {item.status === 'uploading' && <Spinner size={12} />}
              {item.status === 'done' && <span style={{ color: 'var(--teal-600)' }}>✓</span>}
              {item.status === 'error' && <span style={{ color: 'var(--red-600)' }} title={t(item.error)}>✗ {t(item.error)}</span>}
              <button type="button" title={t('Remove')}
                onClick={() => setItems(p => p.filter(i => i.key !== item.key))}
                style={{ background: 'none', border: 'none', color: 'var(--red-500, #ef4444)', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
